import React from 'react';
import { jsx } from '@emotion/core';
import { Formik, Form, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import Modal from '../../../components/modal/Modal';
import Input from '../../../components/input/Input';
import { IModal } from '../../../utils/types';
import theme from '../../../theme/theme';
import UpdateHandlers from '../../../utils/handlers';
import {
  useUpdate_Identification_TypeMutation,
  useAdd_Identification_TypeMutation,
} from '../../../generated/graphql';
import Flex from '../../../components/layout/Flex';
import ErrorMessage from '../../../components/ErrorMessage';
import { useAuthState } from '../../../context/auth';
/**@jsx jsx*/

interface ValueProps {
  name: string;
}

type IDentificationTypeModal = IModal & {
  identificationType?: { id: number; name: string };
};

const IdentificationTypeSchema = Yup.object().shape({
  name: Yup.string().required('required'),
});

const IdentificationTypeModal: React.FC<IDentificationTypeModal> = ({
  onClose,
  identificationType,
}) => {
  const { userId } = useAuthState();
  const [initialValues, setInitialValues] = React.useState<ValueProps>({
    name: '',
  });

  const [
    addIdentificationType,
    {
      data: addIdentificationTypeResult,
      loading: addIdentificationTypeLoading,
      error: addIdentificationTypeError,
    },
  ] = useAdd_Identification_TypeMutation();

  const [
    updateIdentificationType,
    {
      data: updateIdentificationTypeResult,
      loading: updateIdentificationTypeLoading,
      error: updateIdentificationTypeError,
    },
  ] = useUpdate_Identification_TypeMutation();

  React.useEffect(() => {
    if (identificationType) {
      setInitialValues({
        name: identificationType.name,
      });
    }
  }, [identificationType]);

  const addNewIdentificationType = (values: ValueProps) => {
    addIdentificationType({
      variables: {
        changes: [
          {
            name: values.name,
            created_by: userId,
          },
        ],
      },
      update: UpdateHandlers.addGender,
    }).catch(() => {});
  };

  const upateExistingIdentificationType = (values: ValueProps) => {
    updateIdentificationType({
      variables: {
        id: identificationType?.id,
        changes: {
          name: values.name,
          modified_by: userId,
        },
      },
    }).catch(() => {});
  };

  const handleSubmit = (
    values: ValueProps,
    { setSubmitting }: FormikHelpers<ValueProps>
  ) => {
    if (identificationType) {
      upateExistingIdentificationType(values);
    } else {
      addNewIdentificationType(values);
    }
    setSubmitting(false);
  };

  const loading = !!(
    addIdentificationTypeLoading || updateIdentificationTypeLoading
  );
  const error = !!(addIdentificationTypeError || updateIdentificationTypeError);
  const success = !!(
    addIdentificationTypeResult?.insert_identification_type?.returning
      ?.length ||
    updateIdentificationTypeResult?.update_identification_type?.returning
      ?.length
  );

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={IdentificationTypeSchema}
      enableReinitialize>
      {(formikProps) => (
        <Form>
          <Modal
            onClose={onClose}
            cancel={{ label: 'Cancel', function: onClose }}
            confirm={{
              type: 'submit',
              label: `${identificationType ? 'Update' : 'Add'}`,
              loading: formikProps.isSubmitting || loading,
              icon: {
                name: 'plus',
                color: theme.colors.white,
                position: 'after',
              },
            }}
            icon={{ name: 'building-columns' }}
            title={`${
              identificationType ? 'Update' : 'Add'
            } Identification Type`}>
            <React.Fragment>
              {error && (
                <Flex
                  jc="center"
                  css={{ color: theme.colors.red[500] }}
                  className="add-failed">
                  Operation failed...please try again
                </Flex>
              )}
              {success && (
                <Flex
                  jc="center"
                  css={{ color: theme.colors.green[500] }}
                  className="add-success">
                  {`Identification Type ${
                    identificationType ? 'updated' : 'added'
                  } successfully...`}
                </Flex>
              )}
              <div>
                <Input
                  type="text"
                  name="name"
                  placeholder="Enter name"
                  label="Identification Type"
                  onChange={formikProps.handleChange}
                  onBlur={formikProps.handleBlur}
                  value={formikProps.values.name}
                />
                <ErrorMessage name="name" />
              </div>
            </React.Fragment>
          </Modal>
        </Form>
      )}
    </Formik>
  );
};

export default IdentificationTypeModal;
